import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import SEO from '../../components/common/SEO';
import theme from '../../theme';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto;
`;

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ContactForm = styled.form`
  background-color: ${theme.colors.white};
  padding: 2rem;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
`;

const FormTitle = styled.h2`
  font-size: ${theme.fontSizes.large};
  margin-bottom: 1.5rem;
  color: ${theme.colors.secondary};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.small};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 163, 224, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.small};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 163, 224, 0.2);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.small};
  height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 163, 224, 0.2);
  }
`;

const SubmitButton = styled.button`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 0.75rem 2rem;
  border: none;
  border-radius: ${theme.borderRadius};
  font-weight: 600;
  cursor: pointer;
  transition: ${theme.transition};
  
  &:hover {
    background-color: ${theme.colors.accent};
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    background-color: ${theme.colors.lightGray};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SuccessMessage = styled.div`
  background-color: #e1f7e1;
  color: #2e7d32;
  padding: 1rem;
  border-radius: ${theme.borderRadius};
  margin-bottom: 1.5rem;
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: ${theme.borderRadius};
  margin-bottom: 1.5rem;
`;

const ContactInfo = styled.div`
`;

const InfoTitle = styled.h2`
  font-size: ${theme.fontSizes.large};
  margin-bottom: 1.5rem;
  color: ${theme.colors.secondary};
`;

const ContactInfoList = styled.div`
  margin-bottom: 2rem;
`;

const ContactInfoItem = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
`;

const ContactIconContainer = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  background-color: ${theme.colors.lightGray};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: ${theme.colors.primary};
  font-size: 1.2rem;
`;

const ContactText = styled.div`
  h4 {
    margin-bottom: 0.5rem;
    font-size: ${theme.fontSizes.medium};
  }
  
  p {
    color: ${theme.colors.darkGray};
    line-height: 1.5;
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const OfficeLocations = styled.div`
  margin-top: 3rem;
`;

const OfficeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const OfficeCard = styled.div`
  background-color: ${theme.colors.white};
  padding: 1.5rem;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
`;

const OfficeTitle = styled.h4`
  font-size: ${theme.fontSizes.medium};
  margin-bottom: 1rem;
  color: ${theme.colors.secondary};
  
  &:after {
    content: '';
    display: block;
    width: 30px;
    height: 2px;
    background-color: ${theme.colors.primary};
    margin-top: 0.5rem;
  }
`;

const OfficeAddress = styled.p`
  margin-bottom: 1rem;
  line-height: 1.6;
`;

const OfficeContacts = styled.div`
  margin-top: 1rem;
  
  a {
    display: block;
    color: ${theme.colors.primary};
    margin-bottom: 0.5rem;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const MapSection = styled.section`
  margin-top: 5rem;
`;

const MapTitle = styled.h2`
  text-align: center;
  margin-bottom: 3rem;
  font-size: ${theme.fontSizes.xlarge};
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const MapContainer = styled.div`
  height: 450px;
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ContactPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: false
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const subjectOptions = [
    { value: 'demo', label: t('contact.form.subjects.demo') },
    { value: 'partnership', label: t('contact.form.subjects.partnership') },
    { value: 'support', label: t('contact.form.subjects.support') },
    { value: 'sales', label: t('contact.form.subjects.sales') },
    { value: 'other', label: t('contact.form.subjects.other') }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: false });

    const selectedSubject = subjectOptions.find(o => o.value === formData.subject);

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone || '-',
      company: formData.company || '-',
      subject: selectedSubject ? selectedSubject.label : formData.subject,
      message: formData.message
    };

    emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY)
      .then(() => {
        setFormStatus({ submitting: false, success: true, error: false });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      })
      .catch(() => {
        setFormStatus({ submitting: false, success: false, error: true });
      });
  };
  
  return (
    <>
      <SEO 
        titleKey="contact.title"
        descriptionKey="contact.metaDescription"
      />
      
      <ContactSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t('contact.title')}</PageTitle>
            <PageSubtitle>
              {t('contact.subtitle')}
            </PageSubtitle>
          </PageHeader>
          
          <ContactContainer>
            <ContactForm onSubmit={handleSubmit}>
              <FormTitle>{t('contact.formTitle')}</FormTitle>
              
              {formStatus.success && (
                <SuccessMessage>
                  {t('contact.form.success')}
                </SuccessMessage>
              )}
              
              {formStatus.error && (
                <ErrorMessage>
                  {t('contact.form.error')}
                </ErrorMessage>
              )}
              
              <FormGroup>
                <Label htmlFor="name">{t('contact.form.name')}</Label>
                <Input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">{t('contact.form.email')}</Label>
                <Input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="phone">{t('contact.form.phone')}</Label>
                <Input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="company">{t('contact.form.company')}</Label>
                <Input 
                  type="text" 
                  id="company" 
                  name="company" 
                  value={formData.company} 
                  onChange={handleChange} 
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="subject">{t('contact.form.subject')}</Label>
                <Select 
                  id="subject" 
                  name="subject" 
                  value={formData.subject} 
                  onChange={handleChange} 
                  required
                >
                  <option value="">{t('contact.form.selectSubject')}</option>
                  {subjectOptions.map((option, index) => (
                    <option key={index} value={option.value}>{option.label}</option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="message">{t('contact.form.message')}</Label>
                <Textarea 
                  id="message" 
                  name="message" 
                  value={formData.message} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <SubmitButton type="submit" disabled={formStatus.submitting}>
                {formStatus.submitting ? t('contact.form.submitting') : t('contact.form.submit')}
              </SubmitButton>
            </ContactForm>
            
            <ContactInfo>
              <InfoTitle>{t('contact.info.title')}</InfoTitle>
              
              <ContactInfoList>
                <ContactInfoItem>
                  <ContactIconContainer>✉️</ContactIconContainer>
                  <ContactText>
                    <h4>{t('contact.info.email')}</h4>
                    <p><a href={`mailto:${t('contact.info.istanbul.email')}`}>{t('contact.info.istanbul.email')}</a></p>
                  </ContactText>
                </ContactInfoItem>
                
                <ContactInfoItem>
                  <ContactIconContainer>📞</ContactIconContainer>
                  <ContactText>
                    <h4>{t('contact.info.phone')}</h4>
                    <p><a href={`tel:${t('contact.info.istanbul.phone').replace(/\s/g, '')}`}>{t('contact.info.istanbul.phone')}</a></p>
                  </ContactText>
                </ContactInfoItem>
                
              </ContactInfoList>
              
              <OfficeLocations>
                <InfoTitle>{t('contact.info.offices')}</InfoTitle>
                
                <OfficeGrid>
                  <OfficeCard>
                    <OfficeTitle>{t('contact.info.istanbul.title')}</OfficeTitle>
                    <OfficeAddress>
                      {t('contact.info.istanbul.address')}
                    </OfficeAddress>
                    <OfficeContacts>
                      <a href={`tel:${t('contact.info.istanbul.phone').replace(/\s/g, '')}`}>{t('contact.info.istanbul.phone')}</a>
                      <a href={`mailto:${t('contact.info.istanbul.email')}`}>{t('contact.info.istanbul.email')}</a>
                    </OfficeContacts>
                  </OfficeCard>
                  
                  <OfficeCard>
                    <OfficeTitle>{t('contact.info.bursa.title')}</OfficeTitle>
                    <OfficeAddress>
                      {t('contact.info.bursa.address')}
                    </OfficeAddress>
                    <OfficeContacts>
                      <a href={`mailto:${t('contact.info.bursa.email')}`}>{t('contact.info.bursa.email')}</a>
                    </OfficeContacts>
                  </OfficeCard>
                </OfficeGrid>
              </OfficeLocations>
            </ContactInfo>
          </ContactContainer>
        </div>
      </ContactSection>
      
      <MapSection>
        <div className="container">
          <MapTitle>{t('contact.mapTitle')}</MapTitle>
          <MapContainer>
            <iframe
              src="https://maps.google.com/maps?width=100%25&height=450&hl=tr&q=Beybi+Giz+Plaza,+Maslak+Meydan+Sk.+No:1,+Sar%C4%B1yer,+%C4%B0stanbul+(Boranka+Teknoloji+A.%C5%9E.)&t=&z=16&ie=UTF8&iwloc=B&output=embed"
              title={t('contact.mapTitle')}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </MapContainer>
        </div>
      </MapSection>
    </>
  );
};

export default ContactPage; 